import { Image as DatoCmsImage } from 'react-datocms';
import { css, styled } from 'styled-components';

import { Button } from '@src/components/atoms/inputs/button';
import { CheckboxReplacement } from '@src/components/atoms/inputs/checkbox/styles';
import { TextLink } from '@src/components/atoms/text-link';
import { ContentWithLink } from '@src/components/common/formatting/content-with-link';
import { Form } from '@src/components/molecules/form';
import { DividerWrapper } from '@src/components/molecules/form/divider/styles';

import { VerifyResetPasswordWrapper } from '../verify-reset-password/styles';
import { AuthLayoutHeaderProps, AuthLayoutMainProps } from './types';

export const Background = styled.div`
  display: none;
  grid-template-areas: 'center';
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;

  > * {
    grid-area: center;
  }

  img {
    object-fit: cover;
  }

  @media screen and (min-width: 768px) {
    display: grid;
  }
`;

export const ImageWrapper = styled.div`
  display: none;
  position: relative;
  width: 100%;
  height: 151px;
  img {
    object-fit: cover;
  }
  @media (min-height: 720px) {
    display: block;
  }
  @media screen and (min-width: 768px) {
    display: none;
  }
`;

export const Image = styled(DatoCmsImage)`
  height: 100%;
`;

export const Tint = styled.div`
  display: none;
  position: relative;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.42);
  @media screen and (min-width: 768px) {
    display: block;
  }
`;

export const Main = styled.main<Pick<AuthLayoutMainProps, 'background'>>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  position: relative;
  z-index: 1;
  background-color: #ffc9d2;

  ${Background} ~ * {
    color: var(--color-numa-main-black);
  }

  @media screen and (min-width: 768px) {
    background-color: #fff;
    padding: var(--spacing-8);
  }
`;

export const Header = styled.header<Pick<AuthLayoutHeaderProps, 'size'>>`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  text-align: center;

  ${({ size }) =>
    size === 'normal'
      ? css`
          font: var(--font-body--medium);
          gap: var(--spacing-6);
        `
      : css`
          font: var(--font-body--regular);
          gap: var(--spacing-8);
        `}
`;

export const HeaderContent = styled.div`
  font-family: var(--font-lausanne);
  flex: 1 0 100%;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: stretch;
  gap: 16px;
  padding: 0;
  @media screen and (min-width: 768px) {
    padding: 0 var(--spacing-4);
  }
`;

export const LogoWrapper = styled.div<Pick<AuthLayoutHeaderProps, 'size'>>`
  display: inline-flex;
  flex: 0 0 auto;

  > * {
    ${({ size }) =>
      size === 'normal'
        ? css`
            height: 40px;
          `
        : css`
            height: 40px;
          `}
  }

  svg {
    color: currentColor;
  }
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-8);
  font: var(--font-body--regular);

  ${TextLink},
  ${ContentWithLink} {
    text-align: center;
  }

  ${Button} {
    width: 100%;
  }

  ${CheckboxReplacement} {
    border: 1px solid #000;
  }
`;

export const FlexGrow = styled.div`
  flex: 1 0 0;
`;

const CardAuthStyle = css`
  justify-content: start;
  max-width: 480px;
  flex-grow: 1;
  gap: var(--spacing-6);
  background-color: #ffc9d2;
  padding: var(--spacing-5);
  border-radius: 0px;
  z-index: 10;
  font-family: var(--font-lausanne);
  @media screen and (min-width: 768px) {
    justify-content: center;
    flex-grow: 0;
    padding: var(--spacing-10);
    border-radius: 16px;
  }
`;

export const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  height: 100%;

  ${VerifyResetPasswordWrapper} {
    ${CardAuthStyle}
  }

  ${Form} {
    ${CardAuthStyle}
  }

  ${DividerWrapper} {
    color: #757877;
    font-weight: 500;
    font-size: 16px;
    hr {
      border-top: 0px;
      border-bottom: 1px solid #757877;
    }
  }

  ${TextLink} {
    align-self: flex-end;
  }

  ${Footer} ${TextLink} {
    align-self: center;
  }
`;
